import { createSignal, For, Show, createResource, onMount } from "solid-js";
import SessionItem from "./SessionItem";
import {
  listSessions,
  createSession,
  deleteSession as apiDeleteSession,
  SessionAPI,
} from "../api/sessions";
import { useNavigate } from "@solidjs/router";
import { toast } from "solid-toast";
import { useProfile } from "../contexts/ProfileContext";
import { getContactTypeOptions } from "../config/contactTypes";
import { ContactType } from "../types/contact";

type FilterStatus = "all" | "draft" | "in_progress" | "completed";
type FilterContactType = "all" | ContactType;
const SessionsList = () => {
  const [searchTerm, setSearchTerm] = createSignal("");
  const [filterStatus, setFilterStatus] = createSignal<FilterStatus>("all");
  const [filterContactType, setFilterContactType] =
    createSignal<FilterContactType>("all");
  const [useMockData, setUseMockData] = createSignal(false);
  const [isCreating, setIsCreating] = createSignal(false);
  const [filterRespondent, setFilterRespondent] = createSignal("");
  const [filterDateFrom, setFilterDateFrom] = createSignal("");
  const [filterDateTo, setFilterDateTo] = createSignal("");
  const [filterCaseId, setFilterCaseId] = createSignal("");
  const navigate = useNavigate();
  const { profile } = useProfile();
  const contactTypeOptions = () => getContactTypeOptions(profile());
  // Create resource for loading sessions from API
  const [sessionsData, { refetch }] = createResource(
    () => ({
      status: filterStatus(),
      contact_type: filterContactType(),
      search: searchTerm(),
      respondent: filterRespondent(),
      date_from: filterDateFrom(),
      date_to: filterDateTo(),
      case_number: filterCaseId(),
      // Default page size to reduce first-load latency and payload size
      limit: 50,
    }),
    listSessions,
  );

  const filteredSessions = () => sessionsData()?.sessions || [];

  const handleNewSession = async () => {
    setIsCreating(true);
    try {
      const newSession = await createSession({
        title: `New Session - ${new Date().toLocaleDateString()}`,
        contact_type: "",
        source_language: "en-US",
        status: "draft"
      });
      console.log("New session created victor:", newSession);
      refetch();

      navigate(`/sessions/new/${newSession.id}/setup`);
    } catch (error) {
      console.error("Failed to create session:", error);
      toast.error("Failed to create new session");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenSession = (sessionId: string) => {
    // Navigate to session view
    navigate(`/sessions/${sessionId}`);
  };



  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm("Are you sure you want to delete this session?")) {
      return;
    }

    try {
      await apiDeleteSession(sessionId);
      console.log("Deleted session:", sessionId);
      refetch(); // Refresh the list
    } catch (error) {
      console.error("Failed to delete session:", error);
      alert("Failed to delete session. Please try again.");
    }
  };

  const handleTitleUpdated = (sessionId: string, newTitle: string) => {
    console.log("Session title updated:", sessionId, newTitle);
    refetch(); // Refresh the list to show updated title
  };

  return (
    <div class="min-h-screen bg-gray-50">
      {/* Search and Filters */}
      <div class="px-4 py-6">
        {/* Header with New Session Button */}
        <div class="flex justify-between items-center mb-6">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              Transcription Sessions
            </h1>
            <p class="text-sm text-gray-600 mt-1">
              {sessionsData.loading
                ? "Loading sessions..."
                : `${filteredSessions().length} session${
                    filteredSessions().length !== 1 ? "s" : ""
                  }`}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              onClick={handleNewSession}
              disabled={isCreating()}
              class="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating() ? "Creating..." : "+ New Session"}
            </button>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div class="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div class="flex-1">
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm()}
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Case Number Input */}
            <div class="sm:w-36">
              <input
                placeholder="Case #"
                class="w-full px-3 py-2 border rounded-md text-sm"
                value={filterCaseId()}
                onInput={(e) => {
                  // Only allow 5-6 digit numbers
                  const value = e.currentTarget.value
                    .replace(/\D/g, "")
                    .slice(0, 6);
                  setFilterCaseId(value);
                }}
                maxLength={6}
                pattern="[0-9]{5,6}"
              />
            </div>
            {/* Date Range */}
            <div
              class="flex items-center gap-2 min-w-[180px]"
              title="Filter by date range"
            >
              <input
                type="date"
                value={filterDateFrom()}
                onInput={(e) => setFilterDateFrom(e.currentTarget.value)}
                class="px-2 py-1.5 text-sm border rounded-md w-full"
              />
              <span class="text-gray-400 text-sm">→</span>
              <input
                type="date"
                value={filterDateTo()}
                onInput={(e) => setFilterDateTo(e.currentTarget.value)}
                class="px-2 py-1.5 text-sm border rounded-md w-full"
              />
            </div>
            {/* Status Filter */}
            <div class="sm:w-40">
              <select
                value={filterStatus()}
                onChange={(e) =>
                  setFilterStatus(e.currentTarget.value as FilterStatus)
                }
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            {/* Respondent */}
            <div class="sm:w-40">
              <input
                type="text"
                placeholder="👤 Respondent"
                value={filterRespondent()}
                onInput={(e) => setFilterRespondent(e.currentTarget.value)}
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Contact Type Filter */}
            <div class="sm:w-48">
              <select
                value={filterContactType()}
                onChange={(e) =>
                  setFilterContactType(
                    e.currentTarget.value as FilterContactType,
                  )
                }
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {contactTypeOptions().map((option) => (
                  <option value={option.value as FilterContactType}>
                    {option.icon} {option.label}
                  </option>
                ))}
                <Show
                  when={
                    filterContactType() !== "all" &&
                    !contactTypeOptions().some(
                      (option) => option.value === filterContactType(),
                    )
                  }
                >
                  <option value={filterContactType()}>
                    {filterContactType()}
                  </option>
                </Show>
              </select>
            </div>
          </div>
        </div>

        {/* Sessions List */}
        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
          <Show
            when={filteredSessions().length > 0}
            fallback={
              <div class="text-center py-12">
                <div class="text-gray-400 text-6xl mb-4">📝</div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">
                  No sessions found
                </h3>
                <p class="text-gray-600 mb-6">
                  {searchTerm() ||
                  filterStatus() !== "all" ||
                  filterContactType() !== "all"
                    ? "Try adjusting your search or filters"
                    : "Get started by creating your first session"}
                </p>
              </div>
            }
          >
            <div class="divide-y divide-gray-200">

              <For each={filteredSessions()}>
                {(session) => (
                  <SessionItem
                    session={session}
                    onOpen={handleOpenSession}
                    onDelete={handleDeleteSession}
                    onTitleUpdated={handleTitleUpdated}
                  />
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};

export default SessionsList;
